import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, createSeriesMarkers } from 'lightweight-charts';
import './SignalsDashboard.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const DAYS_OPTIONS = [30, 90, 180];

function signalLabel(v) {
  if (v ===  1) return 'BUY';
  if (v === -1) return 'SELL';
  return '—';
}

function signalCls(v) {
  if (v ===  1) return 'buy';
  if (v === -1) return 'sell';
  return 'none';
}

function confluenceCls(n) {
  if (n >= 3) return 'conf-3';
  if (n === 2) return 'conf-2';
  return 'conf-1';
}

function dedupSort(arr) {
  const m = new Map();
  arr.forEach(p => m.set(p.time, p));
  return Array.from(m.values()).sort((a, b) => a.time - b.time);
}

function toUnix(isoStr) {
  return Math.floor(new Date(isoStr).getTime() / 1000);
}

function fmtTime(isoStr) {
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-US', {
    month: 'short', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC',
  }) + ' UTC';
}

// ─────────────────────────────────────────────────────────────────────────────

function SignalsDashboard() {
  const chartContainerRef  = useRef(null);
  const chartRef           = useRef(null);
  const candleRef          = useRef(null);
  const seriesMarkersRef   = useRef(null);  // createSeriesMarkers primitive (v5 API)

  const [pair, setPair]                     = useState('');
  const [availablePairs, setAvailablePairs] = useState([]);
  const [days, setDays]                     = useState(90);
  const [filterS1, setFilterS1]             = useState(true);
  const [filterS2, setFilterS2]             = useState(true);
  const [filterS3, setFilterS3]             = useState(true);
  const [signals,  setSignals]              = useState([]);
  const [loading,  setLoading]              = useState(true);
  const [error,    setError]                = useState(null);
  const [stats,    setStats]                = useState({ total: 0, buys: 0, sells: 0, conf3: 0 });

  // ── Create chart once on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid:   { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair:       { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width:  chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#1F4E8C', downColor: '#C0571F',
      borderVisible: false,
      wickUpColor: '#1F4E8C', wickDownColor: '#C0571F',
    });

    // v5 API: markers attach to the series via a dedicated primitive
    const markers = createSeriesMarkers(candleSeries, []);

    chartRef.current        = chart;
    candleRef.current       = candleSeries;
    seriesMarkersRef.current = markers;

    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        chart.applyOptions({ width, height });
      }
    });
    ro.observe(chartContainerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = candleRef.current = seriesMarkersRef.current = null;
    };
  }, []);

  // ── Fetch available pairs ──────────────────────────────────────────────────
  useEffect(() => {
    fetch('/api/config/pairs')
      .then(r => r.json())
      .then(data => {
        setAvailablePairs(data.pairs || []);
        setPair(data.default || (data.pairs && data.pairs[0]) || '');
      })
      .catch(() => setError('Failed to load trading pairs.'));
  }, []);

  // ── Fetch OHLC candles whenever pair / days change ─────────────────────────
  useEffect(() => {
    if (!pair || !candleRef.current) return;
    const limit = days * 24;
    fetch(`/api/ohlc?pair=${encodeURIComponent(pair)}&limit=${limit}`)
      .then(r => r.json())
      .then(json => {
        const candles = (json.data || []).map(row => ({
          time:  Math.floor(new Date(row.time).getTime() / 1000),
          open:  parseFloat(row.open),
          high:  parseFloat(row.high),
          low:   parseFloat(row.low),
          close: parseFloat(row.close),
        }));
        if (candleRef.current) candleRef.current.setData(dedupSort(candles));
      })
      .catch(() => {});
  }, [pair, days]);

  // ── Fetch signals whenever pair / days change ─────────────────────────────
  useEffect(() => {
    if (!pair) return;
    setLoading(true);
    setError(null);
    fetch(`/api/signals-mock?pair=${encodeURIComponent(pair)}&days=${days}`)
      .then(r => r.json())
      .then(json => {
        setSignals(json.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load signals.');
        setLoading(false);
      });
  }, [pair, days]);

  // ── Apply markers whenever signals or filters change ──────────────────────
  useEffect(() => {
    if (!seriesMarkersRef.current) return;

    const filtered = signals.filter(row =>
      (filterS1 && row.s1 !== 0) ||
      (filterS2 && row.s2 !== 0) ||
      (filterS3 && row.s3 !== 0)
    );

    let buys = 0, sells = 0, conf3 = 0;
    const allMarkers = [];

    filtered.forEach(row => {
      const vals = [];
      if (filterS1 && row.s1 !== 0) vals.push(row.s1);
      if (filterS2 && row.s2 !== 0) vals.push(row.s2);
      if (filterS3 && row.s3 !== 0) vals.push(row.s3);

      const sum  = vals.reduce((a, b) => a + b, 0);
      const conf = vals.length;
      const t    = toUnix(row.time);
      const isBuy = sum >= 0;

      if (isBuy) buys++; else sells++;
      if (conf >= 3) conf3++;

      allMarkers.push({
        time:     t,
        position: isBuy ? 'belowBar' : 'aboveBar',
        color:    isBuy ? '#1F4E8C'  : '#C0571F',
        shape:    isBuy ? 'arrowUp'  : 'arrowDown',
        size:     conf >= 3 ? 3 : conf === 2 ? 2 : 1,
        text:     conf >= 2 ? `×${conf}` : '',
      });
    });

    setStats({ total: filtered.length, buys, sells, conf3 });

    // v5 API: update via the primitive instance, sort by time (required)
    allMarkers.sort((a, b) => a.time - b.time);
    seriesMarkersRef.current.setMarkers(allMarkers);

  }, [signals, filterS1, filterS2, filterS3]);

  // ── Filtered rows for the table ───────────────────────────────────────────
  const tableRows = signals.filter(row =>
    (filterS1 && row.s1 !== 0) ||
    (filterS2 && row.s2 !== 0) ||
    (filterS3 && row.s3 !== 0)
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="signals-dashboard">

      {/* ── Controls bar ─────────────────────────────────────────── */}
      <div className="signals-controls">

        <label htmlFor="sig-pair-select" className="pair-label">Pair</label>
        <select
          id="sig-pair-select"
          value={pair}
          onChange={e => setPair(e.target.value)}
          className="pair-select"
          disabled={availablePairs.length === 0}
        >
          {availablePairs.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <div className="sig-days-group">
          {DAYS_OPTIONS.map(d => (
            <button
              key={d}
              className={`sig-days-btn${days === d ? ' active' : ''}`}
              onClick={() => setDays(d)}
            >
              {d}D
            </button>
          ))}
        </div>

        <div className="sig-divider" />

        <div className="sig-filter-group">
          <span className="sig-filter-label">Strategies:</span>
          {[
            { key: 'S1', label: 'S1 RSI', val: filterS1, set: setFilterS1, color: '#3A17A1' },
            { key: 'S2', label: 'S2 EMA', val: filterS2, set: setFilterS2, color: '#A16D17' },
            { key: 'S3', label: 'S3 BB',  val: filterS3, set: setFilterS3, color: '#1776A1' },
          ].map(({ key, label, val, set, color }) => (
            <label
              key={key}
              className={`sig-strat-toggle${val ? ' active' : ''}`}
              style={{ '--strat-color': color }}
            >
              <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} />
              <span className="sig-strat-dot" />
              {label}
            </label>
          ))}
        </div>

        <div className="sig-divider" />

        <div className="sig-stats">
          <span className="sig-stat-badge sig-stat-total">{stats.total} signals</span>
          <span className="sig-stat-badge sig-stat-buy">▲ {stats.buys} BUY</span>
          <span className="sig-stat-badge sig-stat-sell">▼ {stats.sells} SELL</span>
          {stats.conf3 > 0 && (
            <span className="sig-stat-badge sig-stat-conf3">★ {stats.conf3} confluence</span>
          )}
          {loading && <span className="sig-loading-dot" />}
        </div>
      </div>

      {/* ── Price chart with markers ──────────────────────────────── */}
      <div className="sig-chart-wrapper">
        {loading && (
          <div className="chart-overlay">
            <div className="spinner" />
            <span>Loading signals…</span>
          </div>
        )}
        {error && !loading && (
          <div className="chart-overlay error">
            <span>⚠️ {error}</span>
          </div>
        )}
        <div className="sig-chart-label">Price (1H) · ▲ BUY · ▼ SELL · ×N = N strategies agree</div>
        <div ref={chartContainerRef} className="sig-chart-container" />
      </div>

      {/* ── Signal history table ──────────────────────────────────── */}
      <div className="sig-table-wrapper">
        <div className="sig-table-header">
          <span className="sig-table-title">Signal History</span>
          <span className="sig-table-subtitle">{tableRows.length} events · newest first</span>
        </div>
        {tableRows.length === 0 && !loading ? (
          <div className="sig-empty">No signals for the selected pair / window / strategies.</div>
        ) : (
          <div className="sig-table-scroll">
            <table className="sig-table">
              <thead>
                <tr>
                  <th>Time (UTC)</th>
                  <th>Pair</th>
                  <th>Close</th>
                  <th>S1 RSI Pullback</th>
                  <th>S2 EMA Cross</th>
                  <th>S3 BB Reversion</th>
                  <th>Confluence</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => {
                  const conf = [row.s1, row.s2, row.s3].filter(v => v !== 0).length;
                  return (
                    <tr key={i} className={conf >= 3 ? 'row-conf-3' : conf === 2 ? 'row-conf-2' : ''}>
                      <td className="cell-time">{fmtTime(row.time)}</td>
                      <td className="cell-pair">{row.pair}</td>
                      <td className="cell-close">
                        {row.close.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                      </td>
                      <td>
                        {filterS1 && row.s1 !== 0
                          ? <span className={`sig-badge sig-${signalCls(row.s1)}`}>{signalLabel(row.s1)}</span>
                          : <span className="sig-badge sig-none">—</span>}
                      </td>
                      <td>
                        {filterS2 && row.s2 !== 0
                          ? <span className={`sig-badge sig-${signalCls(row.s2)}`}>{signalLabel(row.s2)}</span>
                          : <span className="sig-badge sig-none">—</span>}
                      </td>
                      <td>
                        {filterS3 && row.s3 !== 0
                          ? <span className={`sig-badge sig-${signalCls(row.s3)}`}>{signalLabel(row.s3)}</span>
                          : <span className="sig-badge sig-none">—</span>}
                      </td>
                      <td>
                        <span className={`sig-conf-badge ${confluenceCls(conf)}`}>
                          {'★'.repeat(conf)}{'☆'.repeat(3 - conf)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}

export default SignalsDashboard;
